import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { Button, Icon } from "aible";
import { useDispatch, useSelector } from "types/store";
import { isEnabled } from "utils/featureFlags";
import { isRemoteSourceContent } from "types/relation";
import { WIDE_DATASET_COLUMN_COUNT, fetchDatasetWithoutDetails, getDataset, deleteDataset, getIsShellDataset } from "containers/Home/reducer";
import { getAccountName } from "containers/Accounts/reducer";
import { reindexDataset } from "containers/Upload/reducer";
import { DATA_SOURCES } from "containers/SchemaMonitor/reducer";
import { getAiReadyMetrics } from "containers/Setup/reducer";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { DATA_SOURCES as LINK_DATA_SOURCES } from "components/View/DatasetLink";
import DataReadinessPanel from "components/Tiles/Sense/DataReadinessPanel";
import MiniInsightsPanel from "components/Tiles/Sense/MiniInsightsPanel";
import OutcomePanel from "components/Tiles/Sense/OutcomePanel";
import ActionsPanel from "components/Tiles/Sense/ActionsPanel";
import ErrorTile from "components/Tiles/Sense/ErrorTile";
import LineagePanel from "components/Tiles/Sense/LineagePanel";
import DataEvaluatedPanel from "components/Tiles/Sense/DataEvaluatedPanel";
import RelationTile from "components/Tiles/Sense/Relation/RelationTile";
import DataValidationPanel from "components/Tiles/Sense/DataValidationPanel";
import SettingsPanel from "components/Tiles/Sense/SettingsPanel";
import CorpusTile from "components/Tiles/Sense/CorpusTile";
import KpiWhatsChangedOptions from "components/Tiles/Sense/KpiWhatsChangedOptions";
import BoomiIcon from "images/nConnectors/boomi.svg";
import CSVIcon from "images/csv_white.svg";
import CSVIconGray from "images/csv_light_grey.svg";
import PinnedIcon from "images/Sense-Icons/pinned-white.svg";
import UnpinnedIcon from "images/SenseIcons/thumbtack-regular.svg";
import PinnedHoverIcon from "images/Sense-Icons/unpin-white.svg";
import TrashIcon from "images/SenseIcons/trash-regular.svg";
import RefreshIcon from "images/Arrow Rotate Right Icon White.svg";
import styles from "./style.module.css";
import { updateIsBoomiLinkMode } from "containers/View/reducer";
import { getIsTableauMode } from "containers/App/reducer";
import { getIsPinned, updateIsPinned, updateTileState } from "containers/Sense/reducer";
const ShellSenseTile = ({ dataset }) => {
    const dispatch = useDispatch();
    const noFileError = dataset && dataset.errors && dataset.errors.find((record) => record.message === "Didn't detect properly as a CSV!");
    const inProgress = dataset.status_name === "RUNNING";
    const splitUrl = dataset.source.url.split("/");
    const uploadId = splitUrl[splitUrl.length - 2];
    const uploadUrl = `${window.location.origin}/upload/${uploadId}`;
    return (React.createElement("div", { className: styles.senseAll },
        React.createElement("div", { className: styles.openOverview, style: { zIndex: 10 } },
            React.createElement("div", { className: styles.shellTitle },
                React.createElement("div", { className: styles.sIcon },
                    React.createElement(FontAwesomeIcon, { icon: faBell, style: { color: "rgb(200, 202, 212)" } })),
                React.createElement("div", { style: { fontWeight: "bold" } }, noFileError
                    ? "No file has been uploaded. Please upload a file and try refreshing the tile"
                    : "This is a placeholder dataset. Please upload your data and refresh")),
            React.createElement("div", { className: styles.shellUrlWrapper },
                React.createElement("div", null, "Dataset Upload URL:"),
                React.createElement("a", { href: uploadUrl, target: "_blank", rel: "noreferrer" }, uploadUrl)),
            React.createElement("div", { style: { marginTop: "20px" } },
                React.createElement(Button, { onClick: () => dispatch(reindexDataset(dataset.id)), disabled: inProgress }, "Refresh Data")))));
};
const WideDatasetWarningPanel = ({ datasetId }) => {
    const dispatch = useDispatch();
    const [isLoading, updateIsLoading] = useState(false);
    return (React.createElement("div", { className: styles.overview, style: {
            maxHeight: "200px",
            width: "100%",
            overflow: "hidden",
            borderColor: "transparent",
            cursor: "default"
        } }, isLoading ? (React.createElement("div", { className: styles.dataErrorHeader + " " + styles.isWideDataset },
        React.createElement(LoadingIndicator, { style: { margin: "0px" } }),
        React.createElement("div", { style: { marginLeft: "10px" } }, "Loading Dataset..."))) : (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.senseTitle },
            React.createElement("div", { className: styles.dataErrorHeader + " " + styles.isWideDataset },
                React.createElement("span", null,
                    React.createElement(Icon, { type: "warning", size: 18 })),
                React.createElement("span", { style: { marginLeft: "5px" } }, "This is a really wide dataset"))),
        React.createElement("div", { className: styles.senseErrorBody, style: { marginTop: "10px" } }, `This dataset is wider than ${WIDE_DATASET_COLUMN_COUNT} columns. Using this dataset may create performance issues up to a browser crash.`),
        React.createElement("div", { className: styles.senseErrorBody + " " + styles.isWideDataset, style: { marginTop: "10px" } },
            React.createElement(Button, { type: "secondary", onClick: () => {
                    updateIsLoading(true);
                    return dispatch(fetchDatasetWithoutDetails(datasetId))
                        .catch((e) => console.log(e))
                        .finally(() => updateIsLoading(false));
                } }, "I understand"))))));
};
const DatasetTile = ({ id, isPinned, updateIsPinned, isFullScreen, transform, style, history }) => {
    const dispatch = useDispatch();
    const onDelete = () => dispatch(deleteDataset(Number(id)));
    const dataset = useSelector((state) => getDataset(state, id === "new" ? id : Number(id)));
    const isTableauMode = useSelector((state) => getIsTableauMode(state));
    const cloudAccountName = useSelector((state) => getAccountName(state, dataset?.account_id));
    const aiReadyMetrics = useSelector((state) => getAiReadyMetrics(state, "new", id));
    const aiScore = (aiReadyMetrics?.aiReadinessScore ?? 0) * 10;
    const aiStatus = aiReadyMetrics?.aiReadinessStatus;
    const isError = dataset?.dataset_status === "ERROR";
    const isDone = dataset?.status_name === "DONE";
    const isRunning = dataset?.status_name === "RUNNING";
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const isReady = dataset?.number_of_rows !== -1;
    const isAiReady = isReady && aiStatus !== "NO_SIGNAL";
    const isProcessing = dataset?.status_name !== "DONE";
    const isShell = useSelector((state) => getIsShellDataset(state, Number(id)));
    const noFileError = isError &&
        dataset &&
        dataset.errors &&
        dataset.errors.find((record) => record.message === "Didn't detect properly as a CSV!");
    const content = dataset?.source.content;
    const isBoomi = content && isRemoteSourceContent(content) && content.provider === "BOOMI";
    const retLabel = isBoomi !== true || dataset?.name === undefined ? dataset?.name : dataset?.name.replace(".csv", "");
    const isGray = isWideDatasetWarning;
    const csvDataSource = DATA_SOURCES.find((source) => source?.value === "CSV");
    let datasetIconWhite = csvDataSource?.iconWhite ?? CSVIcon;
    let datasetIconGray = csvDataSource?.iconGray ?? CSVIconGray;
    let datasetTypeLabel = csvDataSource?.shortLabel ?? csvDataSource?.label;
    if (content && isRemoteSourceContent(content)) {
        let datasetSource = DATA_SOURCES.find((source) => source?.value === content.provider);
        if (datasetSource === undefined) {
            datasetSource = LINK_DATA_SOURCES.find((source) => String(source?.value).toUpperCase() === String(content.provider).toUpperCase());
        }
        datasetIconWhite = datasetSource?.iconWhite ?? datasetSource?.image ?? datasetIconWhite;
        datasetIconGray = datasetSource?.iconGray ?? datasetSource?.image ?? datasetIconGray;
        datasetTypeLabel = datasetSource?.shortLabel ?? datasetSource?.label ?? datasetTypeLabel;
    }
    const tileHeaderBackground = isGray ? "#C8CAD4" : isError ? "#C03E5A" : isAiReady ? "#34d399" : "#C8CAD4";
    const showActions = dataset?.outcome !== undefined && isReady && !isWideDatasetWarning && isDone;
    const showKpiOptions = isReady && !isWideDatasetWarning && isDone;
    const showRefresh = isDone || isError;
    const showTransforms = isEnabled("sense-transformations");
    if (dataset?.id === 10494) {
        console.log("Dataset 10494: ", isReady, !isWideDatasetWarning, isDone);
    }
    if (dataset === undefined) {
        // TODO: do better
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", { className: isFullScreen ? styles.senseTileFullScreen : styles.senseTile, id: "dataset_" + dataset?.id, style: style },
        React.createElement("div", { onClick: isEnabled("chat-aible-consumer") && !isEnabled("aible-optimize")
                ? undefined
                : () => {
                    if (isTableauMode) {
                        return console.log("dclick", id);
                    }
                    if (isEnabled("aible-optimize") || isEnabled("aible-explore")) {
                        return history.push("/next/" + dataset?.id + "/new");
                    }
                }, className: !(isEnabled("chat-aible-consumer") && !isEnabled("aible-optimize")) &&
                !isTableauMode &&
                (isEnabled("aible-optimize") || isEnabled("aible-explore"))
                ? styles.tileHeader
                : styles.tileHeaderNoHover, style: isGray
                ? { background: tileHeaderBackground, color: "#4B495B" }
                : isError
                    ? { background: tileHeaderBackground }
                    : isAiReady
                        ? {}
                        : { background: tileHeaderBackground, color: "#4B495B" } },
            React.createElement("div", { className: styles.tFlex },
                React.createElement("img", { src: !isGray && (isError || isAiReady) ? (isBoomi ? BoomiIcon : datasetIconWhite) : datasetIconGray, style: {
                        height: "30px",
                        width: "30px",
                        marginRight: "5px",
                        background: "transparent",
                        transform: isBoomi ? "scale(1.4)" : "none"
                    }, onClick: isBoomi
                        ? (e) => {
                            e.stopPropagation();
                            dispatch(updateIsBoomiLinkMode(true));
                        }
                        : undefined }),
                React.createElement("div", { style: { width: "100%" } },
                    React.createElement("div", { style: { textTransform: "uppercase" } },
                        React.createElement("span", null, isBoomi ? "Boomi" : datasetTypeLabel),
                        React.createElement("span", { style: { margin: "0px 4px" } }, "|"),
                        React.createElement("span", null, "ID: " + dataset?.id),
                        React.createElement("span", { style: { margin: "0px 4px" } }, "|"),
                        React.createElement("span", null, "Cloud Account: " + cloudAccountName)),
                    React.createElement("div", { className: styles.tileHeaderLarge, style: isPinned ? { maxWidth: "90%" } : {} }, retLabel))),
            React.createElement("div", { className: styles.headerActionBox, style: { background: tileHeaderBackground } },
                React.createElement("div", { className: styles.headerButton },
                    React.createElement(Button, { onClick: (e) => {
                            e.stopPropagation();
                            onDelete();
                        }, style: showRefresh ? { padding: "6px", marginRight: "20px" } : { padding: "6px", marginRight: "47px" }, type: "transparent" },
                        React.createElement("img", { className: styles.pinImage, src: TrashIcon }))),
                showRefresh && (React.createElement("div", { className: styles.headerButton },
                    React.createElement(Button, { onClick: (e) => {
                            e.stopPropagation();
                            dispatch(reindexDataset(dataset?.id));
                        }, style: { padding: "6px", marginRight: "2px" }, type: "transparent" },
                        React.createElement("img", { className: styles.pinImage, src: RefreshIcon })))),
                React.createElement("div", { className: styles.headerButton },
                    React.createElement(Button, { onClick: (e) => {
                            e.stopPropagation();
                            updateIsPinned(!isPinned);
                        }, style: { padding: "6px" }, active: isPinned, type: "transparent", className: styles.pinButton },
                        React.createElement("img", { className: styles.pinNoHover, src: isPinned ? PinnedIcon : UnpinnedIcon }),
                        React.createElement("img", { className: styles.pinHover, src: isPinned ? PinnedHoverIcon : UnpinnedIcon })))),
            isPinned && (React.createElement("div", { className: styles.headerButtonPinned },
                React.createElement(Button, { onClick: () => updateIsPinned(!isPinned), style: { padding: "6px" }, active: isPinned, type: "transparent" },
                    React.createElement("img", { src: PinnedIcon, style: {
                            height: "13px",
                            width: "13px"
                        } }))))),
        React.createElement("div", { className: styles.tileBody }, isShell || noFileError ? (React.createElement(ShellSenseTile, { dataset: dataset })) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.senseSection }, isError ? (React.createElement(ErrorTile, { dataset: dataset })) : (React.createElement("div", { className: styles.senseAll },
                React.createElement(OutcomePanel, { datasetId: dataset.id }),
                showActions && React.createElement(ActionsPanel, { datasetId: dataset.id }),
                showKpiOptions && React.createElement(KpiWhatsChangedOptions, { datasetId: dataset.id }),
                showActions && React.createElement(DataReadinessPanel, { datasetId: dataset.id }),
                showActions && React.createElement(MiniInsightsPanel, { datasetId: dataset.id }),
                isWideDatasetWarning && !isProcessing && React.createElement(WideDatasetWarningPanel, { datasetId: dataset.id }),
                React.createElement(DataEvaluatedPanel, { datasetId: dataset.id }),
                showTransforms && (React.createElement(RelationTile, { datasetId: dataset.id, onSubmit: () => {
                        const datasetDiv = document.getElementById("dataset_" + dataset.id);
                        if (datasetDiv) {
                            datasetDiv.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                                inline: "center"
                            });
                            dispatch(updateTileState(dataset.id, { isOpenTransformations: false }));
                        }
                    } })),
                React.createElement(DataValidationPanel, { datasetId: dataset.id }),
                React.createElement(SettingsPanel, { datasetId: dataset.id }),
                showTransforms && React.createElement(LineagePanel, { datasetId: dataset.id })))))))));
};
const SenseTile = ({ id, type, isFullScreen, transform, style, ...routerProps }) => {
    const dispatch = useDispatch();
    const isPinned = useSelector((state) => getIsPinned(state, id, type));
    const handleUpdateIsPinned = (isPinned) => {
        dispatch(updateIsPinned(id, isPinned, type));
    };
    if (type == "CORPUS") {
        return React.createElement(CorpusTile, { corpusId: id, isPinned: isPinned, updateIsPinned: handleUpdateIsPinned });
    }
    return (React.createElement(DatasetTile, { id: id, isPinned: isPinned, updateIsPinned: handleUpdateIsPinned, isFullScreen: isFullScreen, transform: transform, style: style, ...routerProps }));
};
export default withRouter(SenseTile);
